import { useState, useRef } from 'react';
import { useStore } from '../store';
import { ChevronDown, ChevronUp, Link2, Sliders, FileText, Upload, X, Check, BookOpen, Sparkles, Users, Map } from 'lucide-react';

const ACCENT_COLOR = 'rgb(var(--accent-rgb, 59 78 109))';

function StepSlider({
  value,
  min,
  max,
  labels,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  labels: string[];
  onChange: (val: number) => void;
}) {
  const steps = Array.from({ length: max - min + 1 }, (_, i) => i + min);
  const activeIndex = value - min;
  const fillPercent = steps.length > 1 ? (activeIndex / (steps.length - 1)) * 100 : 0;

  return (
    <div className="space-y-1.5">
      <div className="relative h-6 flex items-center">
        {/* Track background */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-gray-200 rounded-full" />
        {/* Track fill */}
        <div
          className="absolute top-1/2 -translate-y-1/2 left-0 h-1 bg-accent rounded-full transition-all duration-150"
          style={{ width: `${fillPercent}%` }}
        />
        {/* Step dots */}
        <div className="relative w-full flex justify-between">
          {steps.map((step, i) => (
            <button
              key={step}
              onClick={() => onChange(step)}
              className={`w-3.5 h-3.5 rounded-full border-2 transition-all duration-150 ${
                i <= activeIndex
                  ? 'bg-accent border-accent scale-100'
                  : 'bg-white border-gray-300 hover:border-accent/50'
              } ${i === activeIndex ? 'scale-125 shadow-sm' : ''}`}
              title={labels[i]}
            />
          ))}
        </div>
      </div>
      <div className="flex justify-between">
        {labels.map((label, i) => (
          <span
            key={i}
            className={`text-[10px] transition-colors cursor-pointer ${
              i === activeIndex ? 'text-accent font-medium' : 'text-gray-400'
            }`}
            onClick={() => onChange(steps[i])}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ChainSettingsPanel() {
  const {
    nuanceSettings, chainConfig, styleOptions, referenceFileName,
    updateNuanceSettings, updateChainConfig, updateStyleOptions, setReferenceStyle, clearReferenceStyle,
    localFiles, selectedDocuments, addNotification,
  } = useStore();
  const [isChainExpanded, setIsChainExpanded] = useState(true);
  const [isNuanceExpanded, setIsNuanceExpanded] = useState(false);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allAvailableFiles = [...(localFiles || []), ...(selectedDocuments || [])].filter(
    (file, index, self) => self.findIndex((f) => f.id === file.id) === index
  );

  const getFilename = (doc: any) => doc.filename || doc.name || 'Document';

  const handleFileSelect = (file: any) => {
    const content = file.content || '';
    if (!content) {
      addNotification('This file has no text content available for style reference', 'info');
    }
    setReferenceStyle(content, getFilename(file));
    setShowFileSelector(false);
  };

  const handleUploadStyleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setReferenceStyle(event.target?.result as string || '', file.name);
      setShowFileSelector(false);
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const optionLabels: { key: keyof typeof styleOptions; label: string }[] = [
    { key: 'copyTone', label: 'Tone & Voice' },
    { key: 'copyFormatting', label: 'Formatting' },
    { key: 'copySentenceStructure', label: 'Sentence Structure' },
    { key: 'copyParagraphLength', label: 'Paragraph Length' },
    { key: 'copyVocabulary', label: 'Vocabulary' },
  ];

  return (
    <div className="space-y-3 mb-6">
      {/* Chain Mode Settings */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <button
          onClick={() => setIsChainExpanded(!isChainExpanded)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Link2 size={14} className="text-accent" />
            <span className="text-xs font-medium text-gray-700">Chain Mode</span>
            {chainConfig.enabled && (
              <span className="text-[10px] px-1.5 py-0.5 bg-accent/15 text-accent rounded font-medium">ON</span>
            )}
          </div>
          {isChainExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {isChainExpanded && (
          <div className="px-4 py-3 space-y-3">
            {/* Enable toggle */}
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-xs text-gray-700">Enable Chain Generation</span>
              <input
                type="checkbox"
                checked={chainConfig.enabled}
                onChange={(e) => updateChainConfig({ enabled: e.target.checked })}
                className="rounded w-4 h-4"
                style={{ accentColor: ACCENT_COLOR }}
              />
            </label>

            {chainConfig.enabled && (
              <>
                {/* Context Depth */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs text-gray-600">Context Depth</label>
                    <span className="text-xs text-accent font-medium">
                      {chainConfig.contextDepth >= 100 ? 'All sections' : `${chainConfig.contextDepth} sections`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={100}
                    value={chainConfig.contextDepth}
                    onChange={(e) => updateChainConfig({ contextDepth: parseInt(e.target.value) })}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{ accentColor: ACCENT_COLOR }}
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>1</span>
                    <span>All</span>
                  </div>
                  {chainConfig.contextDepth > 25 && (
                    <p className="text-[10px] text-amber-600 mt-1.5">
                      High context depth may exceed your model's limit and cause failures. Try "Summary" or "Key Points" format to reduce token usage.
                    </p>
                  )}
                </div>

                {/* Context Format */}
                <div>
                  <label className="text-xs text-gray-600 block mb-1.5">Context Format</label>
                  <select
                    value={chainConfig.contextFormat}
                    onChange={(e) => updateChainConfig({ contextFormat: e.target.value as any })}
                    className="w-full text-xs border border-gray-200 rounded-md px-2.5 py-1.5 bg-white focus:border-accent/40 focus:ring-1 focus:ring-accent/20 outline-none transition-colors"
                  >
                    <option value="full">Full Text</option>
                    <option value="summary">Summary (truncated)</option>
                    <option value="key-points">Key Points</option>
                  </select>
                </div>

                {/* Continue Flow */}
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <span className="text-xs text-gray-700 block">Continue Flow</span>
                    <span className="text-[10px] text-gray-400">Each section flows from the previous</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={chainConfig.continueFlow}
                    onChange={(e) => updateChainConfig({ continueFlow: e.target.checked })}
                    className="rounded w-4 h-4"
                    style={{ accentColor: ACCENT_COLOR }}
                  />
                </label>

                {/* Divider */}
                <div className="border-t border-gray-100 my-2" />

                {/* Story Mode Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <BookOpen size={13} className="text-accent" />
                    <span className="text-xs font-medium text-gray-700">Story Mode</span>
                    {chainConfig.storyMode && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">ENHANCED</span>
                    )}
                  </div>

                  {/* Story Mode Toggle */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <span className="text-xs text-gray-700 block">Enable Story Mode</span>
                      <span className="text-[10px] text-gray-400">Better coherence for creative writing</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={chainConfig.storyMode}
                      onChange={(e) => updateChainConfig({ storyMode: e.target.checked })}
                      className="rounded w-4 h-4"
                      style={{ accentColor: ACCENT_COLOR }}
                    />
                  </label>

                  {chainConfig.storyMode && (
                    <>
                      {/* Generate Outline */}
                      <label className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-1.5">
                          <Map size={11} className="text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-700 block">Plan Story First</span>
                            <span className="text-[10px] text-gray-400">Generate outline before writing</span>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={chainConfig.generateOutline}
                          onChange={(e) => updateChainConfig({ generateOutline: e.target.checked })}
                          className="rounded w-4 h-4"
                          style={{ accentColor: ACCENT_COLOR }}
                        />
                      </label>

                      {/* Track Characters */}
                      <label className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center gap-1.5">
                          <Users size={11} className="text-gray-400" />
                          <div>
                            <span className="text-xs text-gray-700 block">Track Characters</span>
                            <span className="text-[10px] text-gray-400">Maintain character consistency</span>
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          checked={chainConfig.trackCharacters}
                          onChange={(e) => updateChainConfig({ trackCharacters: e.target.checked })}
                          className="rounded w-4 h-4"
                          style={{ accentColor: ACCENT_COLOR }}
                        />
                      </label>

                      {/* Creativity Level */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Sparkles size={11} className="text-gray-400" />
                          <label className="text-xs text-gray-600">Creativity Level</label>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {(['low', 'medium', 'high'] as const).map((level) => (
                            <button
                              key={level}
                              onClick={() => updateChainConfig({ creativityLevel: level })}
                              className={`text-xs py-1.5 rounded-md border transition-all duration-150 ${
                                chainConfig.creativityLevel === level
                                  ? 'bg-accent/10 border-accent/40 text-accent font-medium shadow-sm'
                                  : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                              }`}
                            >
                              {level === 'low' ? 'Focused' : level === 'medium' ? 'Balanced' : 'Creative'}
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {chainConfig.creativityLevel === 'low' && 'More predictable, consistent output'}
                          {chainConfig.creativityLevel === 'medium' && 'Good balance of creativity and consistency'}
                          {chainConfig.creativityLevel === 'high' && 'More varied, imaginative prose'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Writing Nuance Settings */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <button
          onClick={() => setIsNuanceExpanded(!isNuanceExpanded)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Sliders size={14} className="text-accent" />
            <span className="text-xs font-medium text-gray-700">Writing Nuance</span>
          </div>
          {isNuanceExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </button>

        {isNuanceExpanded && (
          <div className="px-4 py-4 space-y-5">
            {/* Style Source */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-2">Style Source</label>
              {referenceFileName ? (
                <div className="flex items-center gap-2 p-2 bg-accent/10 border border-accent/25 rounded-md">
                  <Check size={13} className="text-accent flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-accent truncate">{referenceFileName}</p>
                    <p className="text-[10px] text-accent/70">Style will be matched</p>
                  </div>
                  <button
                    onClick={clearReferenceStyle}
                    className="p-1 text-accent/60 hover:text-red-500 transition-colors"
                    title="Remove style reference"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowFileSelector(!showFileSelector)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-accent border border-accent/30 rounded-md hover:bg-accent/5 transition-colors"
                  >
                    <FileText size={12} />
                    Select File
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Upload size={12} />
                    Upload
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.md,.doc,.docx,.pdf"
                    onChange={handleUploadStyleFile}
                    className="hidden"
                  />
                </div>
              )}

              {/* File selector dropdown */}
              {showFileSelector && (
                <div className="mt-2 border border-gray-200 rounded-md max-h-28 overflow-y-auto bg-white">
                  {allAvailableFiles.length === 0 ? (
                    <p className="text-xs text-gray-400 px-2 py-2 text-center">
                      No files available. Upload files in the Documents tab.
                    </p>
                  ) : (
                    allAvailableFiles.map((file) => (
                      <button
                        key={file.id}
                        onClick={() => handleFileSelect(file)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left border-b border-gray-100 last:border-b-0"
                      >
                        <FileText size={11} className="text-gray-400 flex-shrink-0" />
                        <span className="truncate">{getFilename(file)}</span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Style attributes */}
              {referenceFileName && (
                <div className="mt-2 space-y-1">
                  {optionLabels.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={styleOptions[key]}
                        onChange={() => updateStyleOptions({ [key]: !styleOptions[key] })}
                        className="rounded w-3 h-3"
                        style={{ accentColor: ACCENT_COLOR }}
                      />
                      <span className="text-[11px] text-gray-600 group-hover:text-gray-800">{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Formality */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">Formality</label>
              </div>
              <StepSlider
                value={nuanceSettings.formality}
                min={1}
                max={5}
                labels={['Casual', '', 'Neutral', '', 'Formal']}
                onChange={(val) => updateNuanceSettings({ formality: val })}
              />
            </div>

            {/* Detail Level */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">Detail Level</label>
              </div>
              <StepSlider
                value={nuanceSettings.detailLevel}
                min={1}
                max={5}
                labels={['Concise', '', 'Moderate', '', 'Exhaustive']}
                onChange={(val) => updateNuanceSettings({ detailLevel: val })}
              />
            </div>

            {/* Confidence */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">Confidence</label>
              </div>
              <StepSlider
                value={nuanceSettings.confidence}
                min={1}
                max={5}
                labels={['Tentative', '', 'Balanced', '', 'Assertive']}
                onChange={(val) => updateNuanceSettings({ confidence: val })}
              />
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Perspective */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-2">Perspective</label>
              <div className="grid grid-cols-4 gap-1">
                {([
                  { value: 'neutral', label: 'Neutral' },
                  { value: 'first', label: '1st' },
                  { value: 'second', label: '2nd' },
                  { value: 'third', label: '3rd' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => updateNuanceSettings({ perspective: opt.value })}
                    className={`text-xs py-1.5 rounded-md border transition-all duration-150 ${
                      nuanceSettings.perspective === opt.value
                        ? 'bg-accent/10 border-accent/40 text-accent font-medium shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Paragraph Style */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-2">Paragraph Length</label>
              <div className="grid grid-cols-3 gap-1.5">
                {(['short', 'medium', 'long'] as const).map((style) => (
                  <button
                    key={style}
                    onClick={() => updateNuanceSettings({ paragraphStyle: style })}
                    className={`text-xs py-1.5 rounded-md border transition-all duration-150 ${
                      nuanceSettings.paragraphStyle === style
                        ? 'bg-accent/10 border-accent/40 text-accent font-medium shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    {style.charAt(0).toUpperCase() + style.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Transitions */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-2">Transitions</label>
              <div className="grid grid-cols-3 gap-1.5">
                {(['minimal', 'moderate', 'heavy'] as const).map((val) => (
                  <button
                    key={val}
                    onClick={() => updateNuanceSettings({ transitions: val })}
                    className={`text-xs py-1.5 rounded-md border transition-all duration-150 ${
                      nuanceSettings.transitions === val
                        ? 'bg-accent/10 border-accent/40 text-accent font-medium shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Repetition Handling */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-2">Repetition</label>
              <div className="grid grid-cols-3 gap-1.5">
                {(['allow', 'reduce', 'eliminate'] as const).map((val) => (
                  <button
                    key={val}
                    onClick={() => updateNuanceSettings({ repetitionHandling: val })}
                    className={`text-xs py-1.5 rounded-md border transition-all duration-150 ${
                      nuanceSettings.repetitionHandling === val
                        ? 'bg-accent/10 border-accent/40 text-accent font-medium shadow-sm'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    {val.charAt(0).toUpperCase() + val.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
